import React, { useState } from 'react'
import { BookOpen, ChevronDown, ChevronRight, X } from 'lucide-react'
import { SMR_P, preFieldHasValue } from './smrFormPanelCore'

export function PreField({ label, value, className = '', showBadge = true }) {
  const filled = preFieldHasValue(value)
  return (
    <div className={className}>
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</div>
      <div
        className={`flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 min-h-[2.25rem] ${filled ? 'bg-slate-50' : 'bg-white'}`}
      >
        <span className="flex-1 break-words">{filled ? String(value).trim() : '—'}</span>
        {filled && showBadge && (
          <span className="shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-200/80 text-slate-600">
            Pre-filled
          </span>
        )}
      </div>
    </div>
  )
}

export function Field({ label, value, onChange, multiline, required, type = 'text', readOnly }) {
  if (readOnly) {
    let display = value
    if (display == null || display === '') display = null
    else if (Array.isArray(display)) display = display.filter(Boolean).join(', ')
    else display = String(display)
    return <PreField label={label} value={display} showBadge={false} />
  }
  const base =
    'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0a5169]/30 focus:border-[#0a5169]'
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-700">
        {label}
        {required && <span className="text-red-600 ml-0.5">*</span>}
      </span>
      {multiline ? (
        <textarea className={`${base} mt-1 min-h-[120px]`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} className={`${base} mt-1`} value={value ?? ''} onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  )
}

export function ChipToggle({ options, selected, onToggle, format, readOnly }) {
  const sel = Array.isArray(selected) ? selected : []
  const visible = format === 'rules_2025' ? options : options.filter((o) => !['proliferation_financing', 'sanctions_breach'].includes(o.value))
  if (readOnly) {
    const picked = visible.filter((o) => sel.includes(o.value))
    return (
      <div className="flex flex-wrap gap-2">
        {picked.length ? picked.map((o) => (
          <span
            key={o.value}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-white border border-transparent"
            style={{ background: SMR_P.pri }}
          >
            {o.label}
          </span>
        )) : <span className="text-sm text-slate-500">—</span>}
      </div>
    )
  }
  return (
    <div className="flex flex-wrap gap-2">
      {visible.map((o) => {
        const on = sel.includes(o.value)
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onToggle(o.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              on ? 'text-white border-transparent' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
            style={on ? { background: SMR_P.pri } : {}}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function TagListEditor({ label, tags, onChange, placeholder, readOnly }) {
  const [input, setInput] = useState('')
  const list = Array.isArray(tags) ? tags : []
  if (readOnly) {
    return (
      <div>
        <div className="text-xs font-medium text-slate-700 mb-1">{label}</div>
        {list.length ? (
          <div className="flex flex-wrap gap-1.5">
            {list.map((t) => (
              <span key={t} className="inline-flex px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 text-xs font-medium">
                {t}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-sm text-slate-500">—</div>
        )}
      </div>
    )
  }
  const add = () => {
    const t = input.trim()
    if (!t || list.includes(t)) return
    onChange([...list, t])
    setInput('')
  }
  const remove = (x) => onChange(list.filter((y) => y !== x))
  return (
    <div>
      <div className="text-xs font-medium text-slate-700 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {list.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-slate-100 text-slate-800 text-xs font-medium"
          >
            {t}
            <button type="button" onClick={() => remove(t)} className="p-0.5 rounded hover:bg-slate-200" aria-label={`Remove ${t}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="flex-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
        />
        <button type="button" onClick={add} className="px-3 py-1.5 rounded-md text-sm font-semibold text-white shrink-0" style={{ background: SMR_P.pri }}>
          Add
        </button>
      </div>
    </div>
  )
}

export function CollapsibleHint({ title, expanded, onToggle, children }) {
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/80 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-bold text-slate-700"
      >
        <BookOpen className="w-3.5 h-3.5 shrink-0 text-slate-500" />
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
      </button>
      {expanded && <div className="px-3 pb-3 text-xs text-slate-600 border-t border-slate-100 pt-2 space-y-1">{children}</div>}
    </div>
  )
}

/** AUSTRAC-style info callout (SmrFormPanelV2) */
export function SmrInfoBox({ children, readOnly }) {
  if (readOnly) return null
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/90 px-3 py-2.5 text-xs text-sky-950 leading-relaxed mb-4">
      <span className="font-semibold text-sky-900">Note. </span>
      {children}
    </div>
  )
}

/** Section title with optional Q-line subtitle */
export function SmrSectionTitle({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h3 className="text-[15px] font-bold m-0" style={{ color: SMR_P.pri }}>
        {title}
      </h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5 m-0">{subtitle}</p>}
    </div>
  )
}

export function SmrPartDivider() {
  return <hr className="border-0 border-t border-slate-100 my-5" />
}
